import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import moment from "moment";
import { Quote } from "src/model/class/general classes/quote";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { AlertService } from "src/service/alert.service";
import { DpQuotesService } from "src/service/quotes.service";
import { UploadFileService } from "src/service/upload-file.service";
import { DateValidators } from "src/validators/date-validators";
import { NumericFieldValidators } from "src/validators/numeric-field-validators";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Component({
    selector: "app-form-quote",
    templateUrl: "./form-quote.component.html",
    styleUrls: ["./form-quote.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormQuoteComponent implements OnInit {
  public selectedFile: File = null;

  public formQuote: UntypedFormGroup = null;
  public isFileContentChanged = false;
  public showErrorMessageOnClick = false;

  @Input() currentQuote: Quote;
  @Input() isEditQuote: boolean;
  @Input() isCreateQuote: boolean;
  @Input() deleteFileConfirmationMsg: string;
  @Output() onDownloadFile = new EventEmitter();

  constructor(
    private _fb: UntypedFormBuilder,
    private _alertService: AlertService,
    private _dpQuotesService: DpQuotesService,
    private _uploadFileService: UploadFileService
  ) { }

  ngOnInit() {
    this.initializeFormQuote();
    this.onCurrentQuoteLoadedPatchFormVal();
  }

  /**
   *  Handle the file change
   * @param event  The event
   */
  public onFileChange(event: any): void {
    const files = (event.target as HTMLInputElement).files;
    if (files.length > 0) {
      this.selectedFile = files[0];
    }
  }

  /**
   * Save the added quote
   * @returns
   */
  public async createFormData(): Promise<Quote> {
    if (this.formQuote.valid) {
      const fileData: string = await this._uploadFileService.readFileAsync(
        this.selectedFile
      );

      return this.handleSavingQuote(fileData);
    } else {
      return null;
    }
  }

  /**
   *  Check if the file name is empty
   * @returns true if the file name is empty
   */
  public isFileNameNotEmpty(): boolean {
    if (this.currentQuote && this.currentQuote.filename) {
      return this.currentQuote.filename !== "";
    } else {
      return false;
    }
  }

  /**
   * Sets the first letter of a string to an upperCase
   * @param label string
   * @returns
   */
  public setFirstLetterToUpperCase(label: string): string {
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  /**
   * Delete the file from the quote
   */
  public async onClickDeleteFile(): Promise<void> {
    try {
      const alertResponse: boolean =
        await this._alertService.presentAlertConfirmOrDismiss(
          this.deleteFileConfirmationMsg,
          "",
          ""
        );
      if (alertResponse) {
        const deleteFileResponseData: boolean =
          await this._dpQuotesService.deleteQuoteFile(
            Number(this.currentQuote.id)
          );
        if (deleteFileResponseData) {
          this.currentQuote.filename = "";
          this.isFileContentChanged = true;
          this.formQuote.patchValue({
            uploadedFile: null,
          });
        } else {
          this.isFileContentChanged = false;
        }
      } else {
        this.isFileContentChanged = false;
      }
    } catch (error) {
      this._alertService.presentAlertBillsQuotesCorrespondingMsg(false, "", "");
    }
  }

  /**
   * Emit an even on click download file
   */
  public onClickDownloadFile(): void {
    this.onDownloadFile.emit();
  }
  /**
   * Initialize the form for the quote
   */
  private initializeFormQuote(): void {
    this.formQuote = this._fb.group(
      {
        fromDate: [
          "",
          {
            validators: [
              Validators.required,
              DateValidators.isDateNotEmpty(),
              DateValidators.isDateFormatYYYYMMDDCorrect(),
            ],
          },
        ],
        toDate: [
          "",
          {
            validators: [
              Validators.required,
              DateValidators.isDateNotEmpty(),
              DateValidators.isDateFormatYYYYMMDDCorrect(),
            ],
          },
        ],
        number: ["", Validators.required],
        date: [
          "",

          {
            validators: [
              Validators.required,
              DateValidators.isDateNotEmpty(),
              DateValidators.isDateFormatYYYYMMDDCorrect(),
            ],
          },
        ],
        costXUnit: [
          null,
          {
            validators: [
              Validators.required,
              NumericFieldValidators.isNotCorrectNumericVal,
            ],
          },
        ],
        note: [""],
        uploadedFile: [null],
      },
      { validators: DateValidators.dateRangeValidator }
    );
  }

  /**
   *  Handle creating a new quote
   * @param fileData  The file data
   * @returns
   */
  private handleSavingQuote(fileData: string): Quote {
    const createdQuote: Quote = this.formQuote.value;

    if (fileData) {
      createdQuote.uploadedFile = {
        namefile: this.selectedFile.name,
        data: fileData,
      };
    }
    createdQuote.costXUnit =
      createdQuote.costXUnit === null ? 0 : createdQuote.costXUnit; //this is needed because the backend expect number and not null
    createdQuote.fromDate = StaticClass.isoDateFormatter(createdQuote.fromDate);
    createdQuote.toDate = StaticClass.isoDateFormatter(createdQuote.toDate);
    createdQuote.date = StaticClass.isoDateFormatter(createdQuote.date);
    createdQuote.filename = "";

    if (fileData) {
      createdQuote.filename = this.selectedFile.name;
    } else {
      createdQuote.filename = this.currentQuote?.filename;
      // No new file: the control still holds the name of the attachment already saved, not a file to upload
      createdQuote.uploadedFile = null;
    }

    return createdQuote;
  }

  /**
   *  Format the date to be displayed in the input field on edit
   * @param date  date to format
   * @returns
   */
  private inputFieldDateFromat(date: string): string {
    return moment(date).format(
      MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_INVERSE
    );
  }

  /**
   * Patch the quote form with the current quote
   */
  private onCurrentQuoteLoadedPatchFormVal(): void {
    if (this.currentQuote && Object.keys(this.currentQuote).length > 0) {
      this.formQuote.patchValue(
        {
          fromDate: this.inputFieldDateFromat(this.currentQuote.fromDate),
          toDate: this.inputFieldDateFromat(this.currentQuote.toDate),
          number: this.currentQuote.number,
          date: this.inputFieldDateFromat(this.currentQuote.date),
          costXUnit: this.currentQuote.costXUnit,
          note: this.currentQuote.note,
          uploadedFile: this.currentQuote.filename,
        },
        {
          emitEvent: false,
        }
      );
    }
  }
}
